import { IClient, getDefaultClient } from "./Client.js";
import { EventTarget } from "./events.js";
import { handleResponseError } from "./utils/handleResponseError.js";

const isodate = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,7}))?Z?$/;
function dateReviver(value: any) {
	// New method of parsing dates - much faster
	if (typeof value === "string") {
		if (value.includes("/Date(")) {
			value = new Date(Number(value.substring(6, value.length - 2)));
		} else if (isodate.test(value)) {
			value = new Date(value);
		}
	}
	return value;
}

function reviveTable(table: any) {
	if (Array.isArray(table)) {
		for (let j = 0; j < table.length; j++) {
			let record = table[j];
			let keys = Object.keys(record);
			for (let key of keys) {
				record[key] = dateReviver(record[key]);
			}
		}
	}
}

export type ProcedureParameter = {
	caption?: string;
	hasDefault?: boolean;
	maxLength?: number;
	name: string;
	required?: boolean;
	type?: string;
};

export type ProcedureOptions = {
	articleId?: string;
	parameters?: Array<ProcedureParameter>;
	procedureId: string;
	timeout?: number;
	workingText?: string | null;
	client?: IClient | null;
	transaction?: boolean;
};

export type ProcedureEvent = {
	onBeforeExecute: any;
	onSuccess: any;
	onError: any;
	onComplete: any;
	onCancel: any;
};

export type ProcedureEventParams = { [K in keyof ProcedureEvent]: CustomEvent<ProcedureEvent[K]> };

export abstract class ProcedureBase<TParams, TResult> extends EventTarget<ProcedureEvent> {
	readonly version = __VERSION__;
	readonly options: Readonly<Required<ProcedureOptions>>;
	private values: Partial<TParams>;
	#client: IClient | null = null;
	get client(): IClient {
		return this.#client ?? this.options.client ?? getDefaultClient();
	}
	set client(value: IClient | null) {
		this.#client = value;
	}
	parameterNames: Array<string>;

	constructor(options: ProcedureOptions) {
		super({
			onBeforeExecute: { abortable: true },
			onSuccess: { abortable: false },
			onError: { abortable: false },
			onComplete: { abortable: false },
			onCancel: { abortable: false },
		});

		this.options = {
			articleId: globalThis.af?.article?.id,
			parameters: [],
			timeout: 0,
			workingText: null,
			client: null,
			transaction: true,
			...options,
		};

		this.parameterNames = this.options.parameters.map((p) => p.name.toLowerCase());
		this.values = {};
	}

	protected abstract getRequestUrl(): string;
	protected abstract getRequestBody(params?: TParams): any;

	/**
	 * Executes the stored procedure using the supplied parameters
	 * @param {TParams} parameters The parameters to execute the procedure with
	 * @returns {Promise<TResult>}
	 */
	async execute(parameters?: TParams): Promise<TResult> {
		const controller = new AbortController();

		const response = await this.client.afFetch(this.getRequestUrl(), {
			body: JSON.stringify(this.getRequestBody(parameters)),
			signal: controller.signal,
			timeout: this.options.timeout,
			mode: "cors",
			credentials: "include",
		});

		if (response.ok && response.headers.get("Content-Type")?.includes("application/json")) {
			let data = await response.json();
			if (Object.hasOwn(data, "success")) {
				for (let key in data.success) {
					reviveTable(data.success[key]);
				}

				this.fireEvent("onSuccess", data.success as TResult);
				this.fireEvent("onComplete");

				return data.success as TResult;
			}
		}

		const responseError = await handleResponseError(response);

		if (responseError.canRetry) {
			return await this.execute(parameters);
		}

		this.fireEvent("onError", responseError.message);
		throw new Error(responseError.message);
	}

	/**
	 * Executes the procedure using the parameters stored in the procedure
	 * instance.
	 *
	 * The execution can be stopped in the onBeforeExecute event.
	 * @returns {Promise<TResult>} Data returned from the stored procedure
	 */
	async execute2() {
		if (this.fireEvent("onBeforeExecute", this.values) !== false) {
			return await this.execute(this.values as TParams);
		}
	}

	/**
	 * Get the currently stored value for a parameter
	 * @param {K | string} name Name of the parameter to get
	 */
	parameterValue<K extends keyof TParams>(name: K | string): TParams[K] | undefined;
	/**
	 * Set the internal value for a parameter. This parameter is used
	 * when executing the procedure using execute2
	 * @param {K} name Name of the parameter to set
	 * @param {TParams[K]} value Parameter value
	 */
	parameterValue<K extends keyof TParams>(name: K, value: TParams[K]): void;
	parameterValue<K extends keyof TParams>(name: K | string, value?: TParams[K]): TParams[K] | undefined {
		if (name && value) {
			this.values[name as K] = value;
		}

		return this.values[name as K];
	}

	/**
	 * Returns the parameter values currently stored in the procedure
	 * instance.
	 * @returns {Partial<TParams>} Parameter values
	 */
	getValues(): Partial<TParams> {
		return this.values;
	}

	/**
	 * Returns the parameter definitions for the procedure
	 * @returns {ProcedureParameter[]}
	 */
	getParameters() {
		return [...this.options.parameters];
	}

	/**
	 * Clears the internal parameter values and fires an onCancel
	 * event.
	 *
	 * NB! Does not cancel an execution that has already been started.
	 */
	cancelExecute() {
		this.values = {};
		this.fireEvent("onCancel");
	}

	/**
	 * Checks if any of the procedure parameters are marked as required.
	 * @returns {boolean} True if any of the parameters are required
	 */
	hasRequiredParameters(): boolean {
		const { parameters } = this.options;
		for (let i = 0; i < parameters.length; i++) {
			if (parameters[i].required) {
				return true;
			}
		}

		return false;
	}
}

export class Procedure<TParams, TResult> extends ProcedureBase<TParams, TResult> {
	constructor(options: ProcedureOptions) {
		super(options);
	}

	protected getRequestUrl() {
		return `/exec/${this.options.articleId}/${this.options.procedureId}`;
	}

	protected getRequestBody(parameters?: TParams) {
		return parameters ?? ({} as TParams);
	}
}

export class ProcedureAPI<TParams, TResult> extends ProcedureBase<TParams, TResult> {
	constructor(options: ProcedureOptions) {
		super(options);
	}

	protected getRequestUrl() {
		return `/api/data`;
	}

	protected getRequestBody(parameters?: TParams) {
		parameters = parameters ?? ({} as TParams);

		return {
			operation: "execute",
			resourceName: this.options.procedureId,
			excludeFieldNames: false,
			timeout: this.options.timeout,
			transaction: this.options.transaction,
			...parameters,
		};
	}
}
